/**
 * PROGRESSIVE VERIFICATION GATING. UF_00 section 5.
 *
 * "This is the single most important cross panel rule, because it decides what every screen can and
 * cannot do."
 *
 * ## A gate is not a block
 *
 * UF_00: "When a user attempts an action above their tier, the app does not simply block. It shows a
 * clear gate screen that states what is LOCKED, WHY, WHICH TIER unlocks it, and a SINGLE primary
 * action to start the required verification."
 *
 * The single action is the load-bearing part. A gate that offers five things to do is a gate people
 * abandon, and an abandoned gate is a customer who never finished verifying — which is a compliance
 * outcome as much as a product one.
 *
 * ## A gate is a COURTESY, not a control
 *
 * Nothing here protects anything. The backend refuses the movement (FR-X-ID-006), the identity gate
 * runs on every value movement, and the database refuses the write. This file exists so that a
 * participant finds out BEFORE they compose a payment rather than after, and so the refusal comes
 * with a next step instead of a shrug.
 *
 * If it were the only thing standing between a T0 user and a transfer, the platform's security would
 * be a `useState` in a browser.
 */

import { Link } from 'react-router-dom';
import { atLeast, useSession, type Tier } from '@/lib/session-context';

/** What each tier unlocks, in the words a participant would use. UF_00 section 5. */
const UNLOCKS: Record<Tier, { name: string; needs: string }> = {
  T0: { name: 'Registered', needs: 'Register with your phone and email' },
  T1: {
    name: 'Tier 1',
    needs: 'Verify your identity with a national ID or passport and a selfie',
  },
  T2: {
    name: 'Tier 2',
    needs: 'Add a proof of address and tell us where your funds come from',
  },
  T3: {
    name: 'Business',
    needs: 'Register your business: tax identity, directors, and beneficial owners',
  },
};

/** Where a participant goes to close the gap. One destination, per tier. */
const START_HERE: Record<Tier, string> = {
  T0: '/',
  T1: '/verify/tier1',
  T2: '/verify/tier2',
  // The business portal IS the Tier 3 KYB surface (the company and its people). There is no separate
  // /verify/business route, and pointing here rather than at a dead path is the honest destination.
  T3: '/business',
};

export function TierGateNotice({ required, what }: { required: Tier; what: string }) {
  const { tier } = useSession();
  const target = UNLOCKS[required];

  return (
    <div className="rounded-card border border-border bg-surface p-6 text-center">
      <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-warning/10">
        <svg className="h-5 w-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 0h10.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5v-6a1.5 1.5 0 0 1 1.5-1.5Z"
          />
        </svg>
      </div>

      {/* WHAT is locked. Named plainly, so nobody has to work out what they just failed to do. */}
      <h3 className="font-semibold text-ink">{what}</h3>

      {/* WHY, and WHICH TIER. Not "insufficient permissions": the actual step that is missing. */}
      <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
        This needs <strong className="font-medium text-ink">{target.name}</strong> verification. You
        are at {UNLOCKS[tier].name} today.
      </p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-ink-muted">{target.needs}.</p>

      {/* ONE primary action. */}
      <Link
        to={START_HERE[required]}
        className="mt-5 inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
      >
        Continue verification
      </Link>

      <p className="mt-3 text-xs text-ink-muted">
        Most people finish this in a few minutes. Nothing you have already done is lost.
      </p>
    </div>
  );
}

/**
 * Wrap anything that needs a tier.
 *
 *     <TierGate requires="T2" what="Sending money">
 *       <SendForm />
 *     </TierGate>
 *
 * While the session is still loading it renders NOTHING rather than the gate. Flashing "you are not
 * verified" at a fully verified user for 200ms, every time they open the app, teaches them that the
 * app is wrong about them — and then they ignore it when it is right.
 */
export function TierGate({
  requires,
  what,
  children,
}: {
  requires: Tier;
  what: string;
  children: React.ReactNode;
}) {
  const { tier, loading } = useSession();

  if (loading) return null;
  if (atLeast(tier, requires)) return <>{children}</>;

  return <TierGateNotice required={requires} what={what} />;
}
